import { useState, useEffect } from 'react';
import { useCandidateStore, type Candidate } from '../../../store/candidateStore';
import { useInterviewStore } from '../../../store/interviewStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { useRecruitmentStore } from '../../../store/recruitmentStore';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from '../../../components/Dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator
} from '../../../components/DropdownMenu';
import { 
  Plus, Search, Users, UserPlus, CheckCircle, Calendar, Video, MoreHorizontal, 
  Trash2, Mail, Phone, ExternalLink, Briefcase, UserCheck, ArrowRight, XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_TABS = [
  { id: 'all', label: 'All Applicants' },
  { id: 'new', label: 'New' },
  { id: 'screening', label: 'Screening' },
  { id: 'interview', label: 'In Interview' },
  { id: 'offer', label: 'Offer' },
  { id: 'hired', label: 'Hired' },
  { id: 'rejected', label: 'Rejected' },
];

const SOURCES = ['LinkedIn', 'Company Career Portal', 'Employee Referral', 'Indeed', 'Direct / Email', 'Recruitment Agency'];

export function Candidates() {
  const { candidates, isLoading, fetchCandidates, createCandidate, updateCandidate, deleteCandidate } = useCandidateStore();
  const { createInterview } = useInterviewStore();
  const { employees, fetchEmployees } = useEmployeeStore();
  const { jobs, fetchJobs } = useRecruitmentStore();
  const { initiateOnboarding } = useOnboardingStore();

  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  // New Candidate Form State
  const [newCandidate, setNewCandidate] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roleApplied: '',
    source: 'LinkedIn',
    resumeUrl: '',
    status: 'new',
  });

  // Interview Form State
  const [interviewForm, setInterviewForm] = useState({
    interviewerId: '',
    type: 'Technical Round 1',
    scheduledTime: '',
    durationMinutes: 60,
    feedback: '',
  });

  useEffect(() => {
    fetchCandidates();
    fetchEmployees();
    fetchJobs();
  }, [fetchCandidates, fetchEmployees, fetchJobs]);

  const filteredCandidates = candidates.filter(c => {
    const q = search.toLowerCase().trim();
    const matchesSearch = 
      !q ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.roleApplied.toLowerCase().includes(q);
    const matchesStatus = selectedStatus === 'all' || c.status.toLowerCase() === selectedStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const handleAddCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCandidate.firstName.trim() || !newCandidate.email.trim() || !newCandidate.roleApplied.trim()) {
      toast.error('First name, email, and role applied are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCandidate.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }

    try {
      await createCandidate({
        firstName: newCandidate.firstName.trim(),
        lastName: newCandidate.lastName.trim(),
        email: newCandidate.email.trim().toLowerCase(),
        phone: newCandidate.phone.trim(),
        roleApplied: newCandidate.roleApplied.trim(),
        source: newCandidate.source,
        resumeUrl: newCandidate.resumeUrl.trim(),
        status: newCandidate.status || 'new',
      });
      toast.success('Candidate profile added successfully');
      setIsAddModalOpen(false);
      setNewCandidate({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        roleApplied: '',
        source: 'LinkedIn',
        resumeUrl: '',
        status: 'new',
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to add candidate');
    }
  };

  const handleStatusChange = async (candidate: Candidate, newStatus: string) => {
    try {
      await updateCandidate(candidate.id, { status: newStatus });
      toast.success(`Status updated to ${newStatus.toUpperCase()}`);

      // If moved to Hired, ask/trigger onboarding
      if (newStatus === 'hired') {
        try {
          await initiateOnboarding({
            candidateName: `${candidate.firstName} ${candidate.lastName}`.trim(),
            email: candidate.email,
            department: 'Engineering',
            designation: candidate.roleApplied,
            joiningDate: new Date(Date.now() + 14 * 86400000).toISOString(),
          });
          toast.success('Onboarding workflow automatically initiated');
        } catch (e) {
          // ignore if duplicate
        }
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const openScheduleInterview = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    tomorrow.setHours(14, 0, 0, 0);
    const dateString = tomorrow.toISOString().slice(0, 16);

    setInterviewForm({
      interviewerId: employees.length > 0 ? employees[0].id : '',
      type: 'Technical Round 1',
      scheduledTime: dateString,
      durationMinutes: 60,
      feedback: '',
    });
    setIsInterviewModalOpen(true);
  };

  const handleScheduleInterview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCandidate) return;

    if (!interviewForm.scheduledTime) {
      toast.error('Please select an interview date & time');
      return;
    }

    try {
      await createInterview({
        candidateId: selectedCandidate.id,
        interviewerId: interviewForm.interviewerId || undefined,
        type: interviewForm.type,
        scheduledTime: new Date(interviewForm.scheduledTime).toISOString(),
        durationMinutes: Number(interviewForm.durationMinutes) || 60,
        status: 'scheduled',
      });

      // Update candidate status to interview
      await updateCandidate(selectedCandidate.id, { status: 'interview' });

      toast.success(`Interview scheduled for ${selectedCandidate.firstName}`);
      setIsInterviewModalOpen(false);
      setSelectedCandidate(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to schedule interview');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to remove candidate "${name}"?`)) {
      try {
        await deleteCandidate(id);
        toast.success('Candidate removed');
      } catch (err) {
        toast.error('Failed to remove candidate');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'hired':
        return <Badge variant="success" dot>Hired</Badge>;
      case 'interview':
        return <Badge variant="primary" dot>Interview</Badge>;
      case 'screening':
        return <Badge variant="warning" dot>Screening</Badge>;
      case 'offer':
        return <Badge variant="info" dot>Offer</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="muted" dot>New</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">
            Candidates & Talent Pool
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage applicant profiles, resumes, and interview transitions.
          </p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <Plus className="size-4" /> Add candidate
        </Button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Users className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Total Applicants</p>
              <h3 className="text-foreground font-display text-metric">{candidates.length}</h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
              <UserPlus className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">New / Screening</p>
              <h3 className="text-foreground font-display text-metric">
                {candidates.filter(c => c.status === 'new' || c.status === 'screening').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-info/10 flex items-center justify-center text-info">
              <Video className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">In Interview</p>
              <h3 className="text-foreground font-display text-metric">
                {candidates.filter(c => c.status === 'interview').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
              <CheckCircle className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Hired Candidates</p>
              <h3 className="text-foreground font-display text-metric">
                {candidates.filter(c => c.status === 'hired').length}
              </h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="shadow-xs border-border">
        <CardHeader className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Status Pills */}
            <div className="flex flex-wrap gap-1">
              {STATUS_TABS.map(tab => (
                <Button
                  key={tab.id}
                  variant={selectedStatus === tab.id ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedStatus(tab.id)}
                  className="text-xs h-8"
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            {/* Search */}
            <div className="w-full sm:w-64">
              <Input
                leftIcon={<Search className="size-4 text-muted-foreground" />}
                placeholder="Search candidates or roles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Loading candidates…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-secondary/40 text-muted-foreground font-medium text-xs border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Candidate</th>
                    <th className="px-6 py-3">Role Applied</th>
                    <th className="px-6 py-3">Source</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Applied Date</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredCandidates.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                        No candidates matching the criteria. Click "+ Add candidate" to add one.
                      </td>
                    </tr>
                  ) : (
                    filteredCandidates.map((c) => (
                      <tr key={c.id} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-6 py-3.5">
                          <div className="font-medium text-foreground">
                            {c.firstName} {c.lastName}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                            <span className="flex items-center gap-1"><Mail className="size-3" /> {c.email}</span>
                            {c.phone && <span className="flex items-center gap-1"><Phone className="size-3" /> {c.phone}</span>}
                          </div>
                        </td>
                        <td className="px-6 py-3.5">
                          <span className="font-medium text-foreground">{c.roleApplied}</span>
                        </td>
                        <td className="px-6 py-3.5 text-muted-foreground">
                          {c.source || 'Direct'}
                        </td>
                        <td className="px-6 py-3.5">
                          {getStatusBadge(c.status)}
                        </td>
                        <td className="px-6 py-3.5 text-muted-foreground tabular-nums text-sm">
                          {new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openScheduleInterview(c)}
                              className="text-xs h-7 gap-1"
                            >
                              <Calendar className="size-3.5" /> Interview
                            </Button>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm">
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Move Stage</div>
                                <DropdownMenuItem onClick={() => handleStatusChange(c, 'screening')}>
                                  Mark as Screening
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(c, 'interview')}>
                                  Mark as Interviewing
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(c, 'offer')}>
                                  Extend Offer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(c, 'hired')}>
                                  <CheckCircle className="size-4 text-success mr-2" /> Mark as Hired
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(c, 'rejected')}>
                                  <XCircle className="size-4 text-destructive mr-2" /> Mark as Rejected
                                </DropdownMenuItem>

                                <DropdownMenuSeparator />
                                {c.resumeUrl && (
                                  <DropdownMenuItem onClick={() => window.open(c.resumeUrl, '_blank')}>
                                    <ExternalLink className="size-4 mr-2" /> View Resume
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem destructive onClick={() => handleDelete(c.id, `${c.firstName} ${c.lastName}`)}>
                                  <Trash2 className="size-4 mr-2" /> Remove Candidate
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Candidate Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Candidate</DialogTitle>
            <DialogDescription>
              Record applicant details, applied position, and resume links.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddCandidate} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">First name *</label>
                <Input
                  required
                  placeholder="e.g. Alex"
                  value={newCandidate.firstName}
                  onChange={(e) => setNewCandidate({ ...newCandidate, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Last name</label>
                <Input
                  placeholder="e.g. Miller"
                  value={newCandidate.lastName}
                  onChange={(e) => setNewCandidate({ ...newCandidate, lastName: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Email address *</label>
                <Input
                  required
                  type="email"
                  placeholder="alex.miller@gmail.com"
                  value={newCandidate.email}
                  onChange={(e) => setNewCandidate({ ...newCandidate, email: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Phone number</label>
                <Input
                  placeholder="+1 (555) 000-0000"
                  value={newCandidate.phone}
                  onChange={(e) => setNewCandidate({ ...newCandidate, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Role Applied For *</label>
              {jobs.length > 0 ? (
                <div className="space-y-1.5">
                  <Input
                    required
                    placeholder="e.g. Senior Backend Engineer"
                    value={newCandidate.roleApplied}
                    onChange={(e) => setNewCandidate({ ...newCandidate, roleApplied: e.target.value })}
                    list="active-job-titles"
                  />
                  <datalist id="active-job-titles">
                    {jobs.map(j => (
                      <option key={j.id} value={j.title}>{j.title} ({j.department})</option>
                    ))}
                  </datalist>
                </div>
              ) : (
                <Input
                  required
                  placeholder="e.g. Senior Backend Engineer"
                  value={newCandidate.roleApplied}
                  onChange={(e) => setNewCandidate({ ...newCandidate, roleApplied: e.target.value })}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Application Source</label>
                <select
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={newCandidate.source}
                  onChange={(e) => setNewCandidate({ ...newCandidate, source: e.target.value })}
                >
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Initial Stage</label>
                <select
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={newCandidate.status}
                  onChange={(e) => setNewCandidate({ ...newCandidate, status: e.target.value })}
                >
                  <option value="new">New Applicant</option>
                  <option value="screening">Screening</option>
                  <option value="interview">Interview</option>
                  <option value="offer">Offer</option>
                  <option value="hired">Hired</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Resume / Portfolio Link</label>
              <Input
                placeholder="https://drive.google.com/resume.pdf or LinkedIn URL"
                value={newCandidate.resumeUrl}
                onChange={(e) => setNewCandidate({ ...newCandidate, resumeUrl: e.target.value })}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsAddModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Add Candidate
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Modal */}
      <Dialog open={isInterviewModalOpen} onOpenChange={setIsInterviewModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Interview</DialogTitle>
            <DialogDescription>
              Set up an interview round for {selectedCandidate?.firstName} {selectedCandidate?.lastName}.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleScheduleInterview} className="space-y-4 pt-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Interview Round</label>
              <select
                className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                value={interviewForm.type}
                onChange={(e) => setInterviewForm({ ...interviewForm, type: e.target.value })}
              >
                <option value="Initial Screening">Initial Screening</option>
                <option value="Technical Round 1">Technical Round 1 (Coding / DSA)</option>
                <option value="Technical Round 2">Technical Round 2 (System Design)</option>
                <option value="Managerial & HR Round">Managerial & HR Round</option>
                <option value="Executive Final Round">Executive Final Round</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Assign Interviewer</label>
              <select
                className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                value={interviewForm.interviewerId}
                onChange={(e) => setInterviewForm({ ...interviewForm, interviewerId: e.target.value })}
              >
                <option value="">Select an employee / interviewer</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName} {emp.designation ? `(${emp.designation})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Date & Time *</label>
                <Input
                  required
                  type="datetime-local"
                  value={interviewForm.scheduledTime}
                  onChange={(e) => setInterviewForm({ ...interviewForm, scheduledTime: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Duration</label>
                <select
                  className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  value={interviewForm.durationMinutes}
                  onChange={(e) => setInterviewForm({ ...interviewForm, durationMinutes: Number(e.target.value) })}
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes (1 hour)</option>
                  <option value={90}>90 minutes</option>
                </select>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsInterviewModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Schedule Interview
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
